import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {
  isLoaded as isLoadedProducts,
  PackageModel,
  UnassignedPackageModel,
} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {
  isLoaded,
  SalaryDeductionOccupationClassInfo,
  SalaryDeductionOccupationClassModel,
  UnassignedSalaryDeductionOccupationClassModel,
} from '../SalaryDeductionOccupationClassModel';

type AccidentProps = {
  productType: StringUnion;
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  section125: boolean;
  replacement: boolean;
  policyNumber: PolicyNumber;
  riderCodeList: I.List<string>;
  riderAmounts: I.List<number>;
  packageList: PackageModel;
  baseUnits: StringUnion;
  packageName: StringUnion;
  situsState: StringUnion;
  plan: StringUnion;
  jStamp: boolean;
  offJobAccident: boolean;
  onOffJobAccident: boolean;
  offJobAccidentSickness: boolean;
  continuationOfCoverage: boolean;
  onOffJobAccidentSickness: boolean;
  onOffJobAccidentSicknessSpouse: boolean;
  onOffJobAccidentInsuredSpouse: boolean;
  onOffJobAccidentSicknessInsuredSpouse: boolean;
  returnOfPremium: boolean;
  hsa: StringUnion;
  coverageTier: StringUnion;
  thirdTier: StringUnion;
  df: StringUnion;
  be: StringUnion;
  berUnits: StringUnion;
  oprUnits: StringUnion;
  accTreatmentUcUnits: StringUnion;
  emerRoomSerUnits: StringUnion;
  outpatientPhyUnits: StringUnion;
  dislocFractureUnits: StringUnion;
  accDeathDisUnits: StringUnion;
  employee: StringUnion;
  spouse: StringUnion;
  occClass: SalaryDeductionOccupationClassModel;
};

export class InvalidGroupAccident {
  public readonly key = 'InvalidGroupAccident';

  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly riderCodeList: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly packageList: PackageModel,
    public readonly baseUnits: StringUnion,
    public readonly packageName: StringUnion,
    public readonly situsState: StringUnion,
    public readonly plan: StringUnion,
    public readonly jStamp: boolean,
    public readonly offJobAccident: boolean,
    public readonly onOffJobAccident: boolean,
    public readonly offJobAccidentSickness: boolean,
    public readonly continuationOfCoverage: boolean,
    public readonly onOffJobAccidentSickness: boolean,
    public readonly onOffJobAccidentSicknessSpouse: boolean,
    public readonly onOffJobAccidentInsuredSpouse: boolean,
    public readonly onOffJobAccidentSicknessInsuredSpouse: boolean,
    public readonly returnOfPremium: boolean,
    public readonly hsa: StringUnion,
    public readonly coverageTier: StringUnion,
    public readonly thirdTier: StringUnion,
    public readonly df: StringUnion,
    public readonly be: StringUnion,
    public readonly berUnits: StringUnion,
    public readonly oprUnits: StringUnion,
    public readonly accTreatmentUcUnits: StringUnion,
    public readonly emerRoomSerUnits: StringUnion,
    public readonly outpatientPhyUnits: StringUnion,
    public readonly dislocFractureUnits: StringUnion,
    public readonly accDeathDisUnits: StringUnion,
    public readonly employee: StringUnion,
    public readonly spouse: StringUnion,
    public readonly occClass: SalaryDeductionOccupationClassModel
  ) {}
}

export class ValidGroupAccident {
  public static createApp({
    productType,
    planType,
    premium,
    calculatedPremium,
    cwa,
    section125,
    replacement,
    policyNumber,
    riderCodeList,
    riderAmounts,
    packageList,
    baseUnits,
    packageName,
    situsState,
    plan,
    jStamp,
    offJobAccident,
    onOffJobAccident,
    offJobAccidentSickness,
    continuationOfCoverage,
    onOffJobAccidentSickness,
    onOffJobAccidentSicknessSpouse,
    onOffJobAccidentInsuredSpouse,
    onOffJobAccidentSicknessInsuredSpouse,
    returnOfPremium,
    hsa,
    coverageTier,
    thirdTier: threeTier,
    df,
    be,
    berUnits,
    oprUnits,
    accTreatmentUcUnits,
    emerRoomSerUnits,
    outpatientPhyUnits,
    dislocFractureUnits,
    accDeathDisUnits,
    employee,
    spouse,
    occClass,
  }: AccidentProps) {
    return this.create(
      productType,
      planType,
      premium,
      calculatedPremium,
      cwa,
      section125,
      replacement,
      policyNumber,
      riderCodeList,
      riderAmounts,
      packageList,
      baseUnits,
      packageName,
      situsState,
      plan,
      jStamp,
      offJobAccident,
      onOffJobAccident,
      offJobAccidentSickness,
      continuationOfCoverage,
      onOffJobAccidentSickness,
      onOffJobAccidentSicknessSpouse,
      onOffJobAccidentInsuredSpouse,
      onOffJobAccidentSicknessInsuredSpouse,
      returnOfPremium,
      hsa,
      coverageTier,
      threeTier,
      df,
      be,
      berUnits,
      oprUnits,
      accTreatmentUcUnits,
      emerRoomSerUnits,
      outpatientPhyUnits,
      dislocFractureUnits,
      accDeathDisUnits,
      employee,
      spouse,
      occClass
    );
  }

  public static create(
    productType: StringUnion,
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    section125: boolean,
    replacement: boolean,
    policyNumber: PolicyNumber,
    riderCodeList: I.List<string>,
    riderAmounts: I.List<number>,
    packageList: PackageModel,
    baseUnits: StringUnion,
    packageName: StringUnion,
    situsState: StringUnion,
    plan: StringUnion,
    jStamp: boolean,
    offJobAccident: boolean,
    onOffJobAccident: boolean,
    offJobAccidentSickness: boolean,
    continuationOfCoverage: boolean,
    onOffJobAccidentSickness: boolean,
    onOffJobAccidentSicknessSpouse: boolean,
    onOffJobAccidentInsuredSpouse: boolean,
    onOffJobAccidentSicknessInsuredSpouse: boolean,
    returnOfPremium: boolean,
    hsa: StringUnion,
    coverageTier: StringUnion,
    thirdTier: StringUnion,
    df: StringUnion,
    be: StringUnion,
    berUnits: StringUnion,
    oprUnits: StringUnion,
    accTreatmentUcUnits: StringUnion,
    emerRoomSerUnits: StringUnion,
    outpatientPhyUnits: StringUnion,
    dislocFractureUnits: StringUnion,
    accDeathDisUnits: StringUnion,
    employee: StringUnion,
    spouse: StringUnion,
    occClass: SalaryDeductionOccupationClassModel
  ) {
    return isNonEmptyString(productType) &&
      planType &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      section125 &&
      replacement &&
      policyNumber &&
      riderCodeList.size > 0 &&
      riderAmounts.size > 0 &&
      isNonEmptyString(packageName) &&
      isNonEmptyString(situsState) &&
      isNonEmptyString(plan) &&
      isLoaded(occClass) &&
      isLoadedProducts(packageList)
      ? new ValidGroupAccident(
          productType,
          planType,
          premium,
          calculatedPremium,
          cwa,
          section125,
          replacement,
          policyNumber,
          riderCodeList,
          riderAmounts,
          packageList,
          baseUnits,
          packageName,
          situsState,
          plan,
          jStamp,
          offJobAccident,
          onOffJobAccident,
          offJobAccidentSickness,
          continuationOfCoverage,
          onOffJobAccidentSickness,
          onOffJobAccidentSicknessSpouse,
          onOffJobAccidentInsuredSpouse,
          onOffJobAccidentSicknessInsuredSpouse,
          returnOfPremium,
          hsa,
          coverageTier,
          thirdTier,
          df,
          be,
          berUnits,
          oprUnits,
          accTreatmentUcUnits,
          emerRoomSerUnits,
          outpatientPhyUnits,
          dislocFractureUnits,
          accDeathDisUnits,
          employee,
          spouse,
          occClass
        )
      : new InvalidGroupAccident(
          productType,
          planType,
          premium,
          calculatedPremium,
          cwa,
          section125,
          replacement,
          policyNumber,
          riderCodeList,
          riderAmounts,
          packageList,
          baseUnits,
          packageName,
          situsState,
          plan,
          jStamp,
          offJobAccident,
          onOffJobAccident,
          offJobAccidentSickness,
          continuationOfCoverage,
          onOffJobAccidentSickness,
          onOffJobAccidentSicknessSpouse,
          onOffJobAccidentInsuredSpouse,
          onOffJobAccidentSicknessInsuredSpouse,
          returnOfPremium,
          hsa,
          coverageTier,
          thirdTier,
          df,
          be,
          berUnits,
          oprUnits,
          accTreatmentUcUnits,
          emerRoomSerUnits,
          outpatientPhyUnits,
          dislocFractureUnits,
          accDeathDisUnits,
          employee,
          spouse,
          occClass
        );
  }

  public readonly key = 'ValidGroupAccident';
  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly riderCodeList: I.List<string>,
    public readonly riderAmounts: I.List<number>,
    public readonly packageList: Loaded<object[]>,
    public readonly baseUnits: StringUnion,
    public readonly packageName: StringUnion,
    public readonly situsState: StringUnion,
    public readonly plan: StringUnion,
    public readonly jStamp: boolean,
    public readonly offJobAccident: boolean,
    public readonly onOffJobAccident: boolean,
    public readonly offJobAccidentSickness: boolean,
    public readonly continuationOfCoverage: boolean,
    public readonly onOffJobAccidentSickness: boolean,
    public readonly onOffJobAccidentSicknessSpouse: boolean,
    public readonly onOffJobAccidentInsuredSpouse: boolean,
    public readonly onOffJobAccidentSicknessInsuredSpouse: boolean,
    public readonly returnOfPremium: boolean,
    public readonly hsa: StringUnion,
    public readonly coverageTier: StringUnion,
    public readonly thirdTier: StringUnion,
    public readonly df: StringUnion,
    public readonly be: StringUnion,
    public readonly berUnits: StringUnion,
    public readonly oprUnits: StringUnion,
    public readonly accTreatmentUcUnits: StringUnion,
    public readonly emerRoomSerUnits: StringUnion,
    public readonly outpatientPhyUnits: StringUnion,
    public readonly dislocFractureUnits: StringUnion,
    public readonly accDeathDisUnits: StringUnion,
    public readonly employee: StringUnion,
    public readonly spouse: StringUnion,
    public readonly occClass: Loaded<SalaryDeductionOccupationClassInfo>
  ) {}
}

export type GroupAccident = InvalidGroupAccident | ValidGroupAccident;

export type GroupAccidentOptions<TReturn> = ToPartialMatchOptions<GroupAccident, 'key', TReturn>;

export const matchGroupAccident = <TReturn>(options: GroupAccidentOptions<TReturn>) => (
  app: GroupAccident
): TReturn => {
  switch (app.key) {
    case 'InvalidGroupAccident':
      return options.InvalidGroupAccident ? options.InvalidGroupAccident(app) : options.default(app);
    case 'ValidGroupAccident':
      return options.ValidGroupAccident ? options.ValidGroupAccident(app) : options.default(app);
  }
};

export const EmptyGroupAccident = new InvalidGroupAccident(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  EmptyPolicyNumber,
  I.List<string>(),
  I.List<number>(),
  new UnassignedPackageModel(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  false,
  false,
  false,
  false,
  false,
  false,
  false,
  false,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new UnassignedSalaryDeductionOccupationClassModel()
);

export const changeProductType = (productType: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, productType: NonEmptyString.create(productType)});

export const changePlanType = (planType: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, planType: NonEmptyString.create(planType)});

export const changePremium = (premium: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeSection125 = (section125: boolean) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, section125});

export const changeReplacement = (replacement: boolean) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, replacement, policyNumber: ValidPolicyNumber.create(new EmptyString())});

export const changePolicyNumber = (policyNumber: StringUnion) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changeRiderCodes = (riderCode: string, index: number) => (app: GroupAccident) => {
  const existingList: I.List<string> = app.riderCodeList;
  const newList = existingList.set(index, riderCode);

  return ValidGroupAccident.createApp({...app, riderCodeList: newList});
};

export const changeRiderAmount = (riderAmount: number, index: number) => (app: GroupAccident) => {
  const existingList: I.List<number> = app.riderAmounts;
  const newList = existingList.set(index, riderAmount);

  return ValidGroupAccident.createApp({...app, riderAmounts: newList});
};

export const changePackageList = (packages: PackageModel) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, packageList: packages});

export const changeBaseUnits = (baseUnits: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, baseUnits: NonEmptyString.create(baseUnits)});

export const changePackage = (packageName: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({
    ...app,
    packageName: NonEmptyString.create(packageName),
    employee: new EmptyString(),
    spouse: new EmptyString(),
    offJobAccident: false,
    offJobAccidentSickness: false,
    onOffJobAccident: false,
    onOffJobAccidentInsuredSpouse: false,
    onOffJobAccidentSickness: false,
    onOffJobAccidentSicknessSpouse: false,
    onOffJobAccidentSicknessInsuredSpouse: false,
    continuationOfCoverage: false,
    returnOfPremium: false,
    berUnits: new EmptyString(),
    oprUnits: new EmptyString(),
    accTreatmentUcUnits: new EmptyString(),
    emerRoomSerUnits: new EmptyString(),
    outpatientPhyUnits: new EmptyString(),
    dislocFractureUnits: new EmptyString(),
    accDeathDisUnits: new EmptyString(),
    df: new EmptyString(),
    be: new EmptyString(),
    hsa: new EmptyString(),
    coverageTier: new EmptyString(),
    thirdTier: new EmptyString(),
  });

export const changeSitusState = (situsState: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, situsState: NonEmptyString.create(situsState)});

export const changePlan = (plan: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({
    ...app,
    plan: NonEmptyString.create(plan),
    packageName: new EmptyString(),
    baseUnits: new EmptyString(),
    employee: new EmptyString(),
    spouse: new EmptyString(),
    offJobAccident: false,
    offJobAccidentSickness: false,
    onOffJobAccident: false,
    onOffJobAccidentInsuredSpouse: false,
    onOffJobAccidentSickness: false,
    onOffJobAccidentSicknessSpouse: false,
    onOffJobAccidentSicknessInsuredSpouse: false,
    continuationOfCoverage: false,
    returnOfPremium: false,
    berUnits: new EmptyString(),
    oprUnits: new EmptyString(),
    accTreatmentUcUnits: new EmptyString(),
    emerRoomSerUnits: new EmptyString(),
    outpatientPhyUnits: new EmptyString(),
    dislocFractureUnits: new EmptyString(),
    accDeathDisUnits: new EmptyString(),
    df: new EmptyString(),
    be: new EmptyString(),
    hsa: new EmptyString(),
    coverageTier: new EmptyString(),
    thirdTier: new EmptyString(),
  });

export const changeOccClass = (occClass: SalaryDeductionOccupationClassModel) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, occClass});

export const changeJStamp = (jStamp: boolean) => (app: GroupAccident) => ValidGroupAccident.createApp({...app, jStamp});

export const changeContinuationOfCoverage = (continuationOfCoverage: boolean) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, continuationOfCoverage});

export const changeReturnOfPremium = (returnOfPremium: boolean) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, returnOfPremium});

export const changeHsa = (hsa: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, hsa: NonEmptyString.create(hsa)});

export const changeCoverageTier = (coverageTier: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, coverageTier: NonEmptyString.create(coverageTier)});

export const changeThirdTier = (thirdTier: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, thirdTier: NonEmptyString.create(thirdTier)});

export const changeBERVersion = (berUnits: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, berUnits: NonEmptyString.create(berUnits)});

export const changeOprUnits = (oprUnits: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, oprUnits: NonEmptyString.create(oprUnits)});

export const changeAccUcUnits = (accTreatmentUcUnits: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, accTreatmentUcUnits: NonEmptyString.create(accTreatmentUcUnits)});

export const changeEmerRoomSerUnits = (emerRoomSerUnits: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, emerRoomSerUnits: NonEmptyString.create(emerRoomSerUnits)});

export const changeOutpatientPhyUnits = (outpatientPhyUnits: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, outpatientPhyUnits: NonEmptyString.create(outpatientPhyUnits)});

export const changeDislocFracUnits = (dislocFractureUnits: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, dislocFractureUnits: NonEmptyString.create(dislocFractureUnits)});

export const changeAccDeathDisUnits = (accDeathDisUnits: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, accDeathDisUnits: NonEmptyString.create(accDeathDisUnits)});

export const changeDFVersion = (df: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, df: NonEmptyString.create(df)});

export const changeBEVersion = (be: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, be: NonEmptyString.create(be)});

export const changeOnOffJobAccident = (onOffJobAccident: boolean) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, onOffJobAccident});

export const changeOnOffJobAccidentSickness = (onOffJobAccidentSickness: boolean) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, onOffJobAccidentSickness});

export const changeOnOffJobAccidentSicknessSpouse = (onOffJobAccidentSicknessInsuredSpouse: boolean) => (
  app: GroupAccident
) => ValidGroupAccident.createApp({...app, onOffJobAccidentSicknessInsuredSpouse});

export const changeOnOffJobAccidentSpouse = (onOffJobAccidentSpouse: boolean) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, onOffJobAccidentInsuredSpouse: onOffJobAccidentSpouse});

export const changeOffJobAccident = (offJobAccident: boolean) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, offJobAccident});

export const changeOffJobAccidentSickness = (offJobAccidentSickness: boolean) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, offJobAccidentSickness});

export const changeEmployee = (employee: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, employee: NonEmptyString.create(employee)});

export const changeSpouse = (spouse: string) => (app: GroupAccident) =>
  ValidGroupAccident.createApp({...app, spouse: NonEmptyString.create(spouse)});

export const plans = [
  {name: 'GVAP1', displayName: 'GVAP1'},
  {name: 'GVA1FD', displayName: 'GVAP1FD'},
  {name: 'GVAP2', displayName: 'GVAP2'},
  {name: 'GVA2FD', displayName: 'GVAP2FD'},
  {name: 'GVAP6', displayName: 'GVAP6'},
];

export const baseUnits = {
  GVAP1: [0.5, 1, 1.5, 2, 2.5, 3],
  GVA1FD: [0.5, 1, 1.5, 2, 2.5, 3],
  GVAP2: [1, 2, 3, 4, 5, 6],
  GVA2FD: [1, 2, 3, 4, 5, 6],
  GVAP6: [1, 1.5, 2, 2.5, 3, 3.5, 4],
};

export type riderCodeAccidentType = {
  accidentType: {code: string; displayName: string; propertyName: string; units: number[]}[];
};

export type riderCodeType = {code: string; displayName: string; propertyName: string; units: number[]};

export const berUnits = {
  GVAP1: [1, 1.5, 2, 2.5, 3],
  GVA1FD: [1, 1.5, 2, 2.5, 3],
  GVAP2: [0, 1, 2, 3, 4, 5, 6],
  GVA2FD: [0, 1, 2, 3, 4, 5, 6],
  GVAP6: [0, 1, 1.5, 2, 2.5, 3, 3.5, 4],
};

export const oprUnits = {
  GVAP2: [0, 1, 2, 3, 4, 5, 6],
  GVA2FD: [0, 1, 2, 3, 4, 5, 6],
  GVAP6: [0, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.25, 3.5, 3.75, 4],
};

export const accTreatmentUcUnits = {
  GVAP6: [0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.25, 3.5, 3.75, 4, 5],
};

export const emerRoomSerUnits = {
  GVAP6: [0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.25, 3.5, 3.75, 4, 5],
};

export const outpatientPhyUnits = {
  GVAP6: [0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.25, 3.5, 3.75, 4, 5],
};

export const dislocFractureUnits = {
  GVAP6: [0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.25, 3.5, 3.5, 3.75, 4, 5],
};

export const accDeathDisUnits = {
  GVAP6: [0, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.25, 3.5, 3.75, 4, 4.5, 5],
};
